<script lang="ts" setup>
export interface PageHeaderContentProps {
    tag?: string | null;
    title: string | null;
    lang?: string | null;
    subtitle?: string | null;
    ctaTitle?: PageHeaderCTA | null;
    textColor?: string;
    ctaScrollTarget?: string | null;
    headingType?: PageHeaderHeadingType | null;
}

const props = withDefaults(defineProps<PageHeaderContentProps>(), {
    tag: null,
    ctaTitle: null,
    textColor: 'white',
    subtitle: null,
    ctaScrollTarget: null,
    headingType: 'h1',
    lang: undefined,
})

const handleClickOnScrollToTarget = (): void => {
    // only on client side
    if (!import.meta.client || !props.ctaScrollTarget) return
    // get target section
    const target = document.querySelector(props.ctaScrollTarget) as HTMLElement
    if (!target) return
    // scroll to the target
    useScrollIntoView(target)

    // Move focus to heading inside target for screen reader users (WCAG 2.4.3)
    // This announces the section title to screen reader users
    // Use longer timeout to ensure lazy-loaded content is rendered
    const moveFocusToTarget = () => {
        // Find the first heading inside the target section
        const heading = target.querySelector('h1, h2, h3, h4, h5, h6') as HTMLElement
        const focusTarget = heading || target

        // Make element focusable if it isn't already
        if (!focusTarget.hasAttribute('tabindex')) {
            focusTarget.setAttribute('tabindex', '-1')
        }
        focusTarget.focus({ preventScroll: true })
    }

    // Try immediately, then retry after delay for lazy-loaded content
    setTimeout(moveFocusToTarget, 100)
    setTimeout(moveFocusToTarget, 800)
}
</script>

<template>
  <div
    :style="`--page-header-text-color: var(--global-color-${props.textColor})`"
    class="page-header-content flex-grow-1 position-relative z-index-5 px-4"
  >
    <Tag
      v-if="props.tag"
      class="mx-auto"
      :decor="true"
      text-color="white"
      background-color="secondary"
    >
      {{ props.tag }}
    </Tag>

    <PageHeaderTitle
      v-if="props.title"
      :type="props.headingType || ''"
      :lang="props.lang || undefined"
      class="text-center mt-4"
    >
      {{ props.title }}
    </PageHeaderTitle>

    <div
      v-if="props.subtitle"
      class="row g-0 justify-content-center"
    >
      <p
        class="page-header-subtitle col-12 col-md-8 col-lg-8 fw-bolder text-center delta mb-0"
      >
        {{ props.subtitle }}
      </p>
    </div>
    <slot name="cta">
      <div
        v-if="props.ctaTitle"
        class="d-flex justify-content-center"
      >
        <!-- When ctaScrollTarget is provided, render as button (not link) since it scrolls instead of navigating -->
        <VButton
          v-if="props.ctaScrollTarget"
          class="mt-8 mt-md-3"
          color="primary"
          size="large"
          type="button"
          :label="props.ctaTitle.text"
          @click="handleClickOnScrollToTarget"
        >
          {{ props.ctaTitle.text }}
        </VButton>
        <!-- When no scroll target, render as link for actual navigation -->
        <VButton
          v-else
          class="mt-8 mt-md-3"
          color="primary"
          size="large"
          :to="props.ctaTitle.link"
          :label="props.ctaTitle.text"
        >
          {{ props.ctaTitle.text }}
        </VButton>
      </div>
    </slot>
  </div>
</template>

<style lang="scss" scoped>
@import "./PageHeaderContent.scss";
</style>
